/**
 * useMediaQuery — subscribe to a CSS media query.
 *
 * Backed by useSyncExternalStore so the value is correct on the very first
 * render (no mobile/desktop flash) and updates only when the query flips.
 */
import { useCallback, useSyncExternalStore } from 'react';

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window.matchMedia !== 'function') return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );

  const getSnapshot = useCallback(
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false),
    [query],
  );

  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Below Tailwind `md` (768px) — the breakpoint used for nav/drawer layouts. */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)');
}

/** Below Tailwind `sm` (640px) — the breakpoint used for bottom sheets/dropdowns. */
export function useIsCompact(): boolean {
  return useMediaQuery('(max-width: 639px)');
}
